import { useQuery } from "@tanstack/react-query";
import { useRouter } from "@tanstack/react-router";
import { ArrowUpRightIcon, LayoutDashboardIcon, SendIcon } from "lucide-react";
import { useMemo, type AnchorHTMLAttributes, type MouseEvent, type ReactNode } from "react";

import { useAccess } from "~/access/AccessProvider";
import { Badge } from "~/components/ui/badge";
import { Button } from "~/components/ui/button";
import { cn } from "~/lib/utils";

import { DASHBOARD_ARTIFACTS_SEGMENT } from "../catalog";
import {
  artifactActionsFor,
  DASHBOARDS_ARTIFACTS_PUBLISH_PERMISSION,
  useArtifactChange,
} from "./ArtifactActions";
import { ArtifactWidget } from "./ArtifactWidget";
import { artifactHeadlineKpis } from "./artifactLayout";
import { artifactDefaultPeriodLabel, defaultArtifactParameterValues } from "./artifactParameters";
import { dashboardArtifactQueryOptions } from "./artifactsReactQuery";
import type {
  ArtifactSpec,
  DashboardArtifactSummary,
  DashboardArtifactVersionSummary,
} from "./artifactTypes";
import { useArtifactNow, useArtifactResolve } from "./useArtifactResolve";

const ARTIFACT_PATH = new RegExp(
  `^/dashboards/${DASHBOARD_ARTIFACTS_SEGMENT}/([a-z0-9][a-z0-9-]*)/?$`,
);

/** Static so Tailwind can see every class. */
const HEADLINE_COLUMNS: Record<number, string> = {
  1: "sm:grid-cols-1",
  2: "sm:grid-cols-2",
  3: "sm:grid-cols-3",
  4: "sm:grid-cols-4",
};

export type ChatDashboardTarget = { path: string; slug: string; version?: number };

/**
 * A link to a dashboard built in chat, on any host: `dashboard_generate`
 * returns the production URL, and the card opens it in this app instead.
 */
export function parseArtifactDashboardHref(href: string | undefined): ChatDashboardTarget | null {
  if (!href) return null;
  let url: URL;
  try {
    url = new URL(href, "https://operators.invalid");
  } catch {
    return null;
  }
  if (url.protocol !== "https:" && url.protocol !== "http:") return null;
  const slug = ARTIFACT_PATH.exec(url.pathname)?.[1];
  if (!slug) return null;
  const raw = url.searchParams.get("version");
  const version = raw !== null && /^[1-9]\d*$/.test(raw) ? Number(raw) : undefined;
  return {
    path: `${url.pathname}${version === undefined ? "" : `?version=${version}`}`,
    slug,
    ...(version === undefined ? {} : { version }),
  };
}

function isPlainLeftClick(event: MouseEvent): boolean {
  return event.button === 0 && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey;
}

/** An inline card for a dashboard link in chat. */
export function ChatDashboardLink(
  props: AnchorHTMLAttributes<HTMLAnchorElement> & {
    children?: ReactNode;
    dashboard: ChatDashboardTarget;
  },
) {
  const { children, dashboard, ...anchorProps } = props;
  void anchorProps;
  return <ChatDashboardCard target={dashboard} fallback={children} />;
}

function ChatDashboardCard(props: { fallback: ReactNode; target: ChatDashboardTarget }) {
  const { target } = props;
  const router = useRouter();
  const query = useQuery(dashboardArtifactQueryOptions(target.slug, target.version));
  const loaded = query.data;
  const published = loaded?.version.published === true;
  const period = loaded ? artifactDefaultPeriodLabel(loaded.spec.parameters) : null;
  const open = (event: MouseEvent) => {
    if (!isPlainLeftClick(event)) return;
    event.preventDefault();
    router.history.push(target.path);
  };

  return (
    <div
      data-chat-dashboard-card=""
      className="my-1 flex max-w-2xl flex-col gap-3 rounded-xl border border-border/70 bg-card p-3.5 shadow-xs/5"
    >
      <div className="flex items-center gap-3">
        <span className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-muted text-muted-foreground">
          <LayoutDashboardIcon className="size-4.5" aria-hidden />
        </span>
        <div className="flex min-w-0 flex-1 flex-col">
          <a
            href={target.path}
            onClick={open}
            className="truncate text-sm font-semibold text-foreground no-underline hover:underline"
          >
            {loaded?.spec.title ?? props.fallback}
          </a>
          <span className="truncate text-xs text-muted-foreground">
            {query.isError
              ? "Could not load the preview"
              : loaded
                ? [
                    `Version ${loaded.version.version}`,
                    published ? "shared with the team" : "not shared yet",
                    period,
                  ]
                    .filter(Boolean)
                    .join(" · ")
                : "Loading…"}
          </span>
        </div>
        {loaded ? (
          <Badge size="sm" variant={published ? "success" : "outline"} className="shrink-0">
            {published ? "Published" : "Draft"}
          </Badge>
        ) : null}
      </div>
      {loaded ? (
        <ChatDashboardHeadline
          artifact={loaded.artifact}
          spec={loaded.spec}
          version={loaded.version}
        />
      ) : null}
      <div className="flex flex-wrap items-center gap-2">
        <Button
          size="sm"
          variant="outline"
          render={<a href={target.path} onClick={open} className="no-underline" />}
        >
          Open
          <ArrowUpRightIcon aria-hidden />
        </Button>
        {loaded ? <ChatDashboardShare artifact={loaded.artifact} version={loaded.version} /> : null}
      </div>
    </div>
  );
}

/**
 * The first KPI row at the dashboard's default filters, with the same widgets
 * and trust popover as the page. It reads the same resolve stream the page
 * does, so opening the dashboard at its defaults is already loaded.
 */
function ChatDashboardHeadline(props: {
  artifact: DashboardArtifactSummary;
  spec: ArtifactSpec;
  version: DashboardArtifactVersionSummary;
}) {
  const { artifact, spec, version } = props;
  const now = useArtifactNow();
  const kpis = useMemo(() => artifactHeadlineKpis(spec), [spec]);
  const values = useMemo(() => defaultArtifactParameterValues(spec.parameters), [spec.parameters]);
  const resolve = useArtifactResolve({
    artifactId: artifact.id,
    now,
    parameters: spec.parameters,
    values,
    versionId: version.id,
  });
  if (kpis.length === 0) return null;
  return (
    <div
      className={cn("grid grid-cols-2 gap-2", HEADLINE_COLUMNS[kpis.length])}
      aria-label="Headline numbers"
    >
      {kpis.map((kpi) => (
        <div key={kpi.id} className="min-w-0" data-artifact-widget={kpi.id}>
          <ArtifactWidget
            artifactTitle={spec.title}
            now={now}
            spec={spec}
            widget={kpi}
            state={resolve.widgetState(kpi.id)}
          />
        </div>
      ))}
    </div>
  );
}

function ChatDashboardShare(props: {
  artifact: DashboardArtifactSummary;
  version: DashboardArtifactVersionSummary;
}) {
  const { artifact, version } = props;
  const access = useAccess();
  const change = useArtifactChange(artifact);
  const { publish } = artifactActionsFor({
    artifact,
    canPublish: access.can(DASHBOARDS_ARTIFACTS_PUBLISH_PERMISSION),
    version,
    versionCount: 0,
    viewerEmail: null,
  });
  if (!publish) return null;
  return (
    <Button
      size="sm"
      disabled={change.isPending}
      onClick={() => change.mutate({ change: "publish", versionId: version.id })}
    >
      <SendIcon aria-hidden />
      {publish === "share" ? "Share with team" : "Publish this version"}
    </Button>
  );
}
